import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { NewsArticle, PaginatedNewsResponse } from '../models/news-article.model';

@Injectable({
  providedIn: 'root',
})
export class NewsService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/news`;

  getNews(
    page: number,
    limit: number,
    search?: string,
    articleType: 'all' | 'news' | 'blog' = 'all',
    timeFilter: 'anytime' | 'day' | 'week' = 'anytime'
  ): Observable<PaginatedNewsResponse> {
    let params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());

    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }

    if (articleType && articleType !== 'all') {
      params = params.set('type', articleType);
    }

    if (timeFilter && timeFilter !== 'anytime') {
      params = params.set('timeFilter', timeFilter);
    }

    return this.http.get<PaginatedNewsResponse>(this.apiUrl, {
      params,
      headers: { 'x-no-cache': 'true' },
    });
  }

  getArticleById(id: string): Observable<NewsArticle> {
    return this.http.get<NewsArticle>(`${this.apiUrl}/${id}`, {
      headers: {
        Accept: 'application/json',
      },
    });
  }

  getArticleBySlug(slug: string): Observable<NewsArticle> {
    return this.http.get<NewsArticle>(`${this.apiUrl}/by-slug/${slug}`, {
      headers: {
        Accept: 'application/json',
      },
    });
  }

  /**
   * Get adjacent articles using the simple approach (more reliable)
   * @param currentArticle The current article to find adjacent articles for
   * @param type Optional filter by article type (news or blog)
   * @returns Observable with an object containing previous and next articles
   */
  getAdjacentArticlesSimple(
    currentArticle: NewsArticle,
    type?: 'news' | 'blog'
  ): Observable<{ previous: NewsArticle | null; next: NewsArticle | null }> {
    let params = new HttpParams()
      .set('currentId', currentArticle.id)
      // Add timestamp to prevent caching
      .set('_t', Date.now().toString());

    if (type) {
      params = params.set('type', type);
    }

    return this.http.get<{ previous: NewsArticle | null; next: NewsArticle | null }>(`${this.apiUrl}/adjacent-simple`, {
      params,
      headers: { 'x-no-cache': 'true', 'Cache-Control': 'no-cache, no-store, must-revalidate' },
    });
  }

  /**
   * Get adjacent articles (previous and next) based on the current article's timestamp
   * @param currentArticle The current article to find adjacent articles for
   * @param type Optional filter by article type (news or blog)
   * @returns Observable with an object containing previous and next articles
   */
  getAdjacentArticles(
    currentArticle: NewsArticle,
    type?: 'news' | 'blog'
  ): Observable<{ previous: NewsArticle | null; next: NewsArticle | null }> {
    let params = new HttpParams()
      .set('currentId', currentArticle.id)
      .set('timestamp', currentArticle.createdAt || currentArticle.updatedAt || '')
      // Add timestamp to prevent caching
      .set('_t', Date.now().toString());

    if (type) {
      params = params.set('type', type);
    }

    return this.http.get<{ previous: NewsArticle | null; next: NewsArticle | null }>(`${this.apiUrl}/adjacent`, {
      params,
      headers: { 'x-no-cache': 'true', 'Cache-Control': 'no-cache, no-store, must-revalidate' },
    });
  }
}
